<template>
    <ManageIntegrationBase
      :integration="integration"
      :show="show"
      :showAuthSuccess="showAuthSuccess"
      :title="$t('companySettings.integrations.googledrive.name')"
      @loadingStateChanged="reEmitLoadingStateChanged"
      @close="emitClose"
    >
      <VForm
        :onSubmit="emitClose"
        data-vv-scope="googledrive-edit"
        ref="form">
        <div slot="row">
          <VGrid>
            <div slot="gridItem" type="12">
              <p><strong>{{ $t('companySettings.integrations.googledrive.settings.folder') }}</strong> {{ googleDriveFolder }}</p>

              <div v-if="integration.client_id">
              <BaseButton
                :disabled="!pickerApiLoaded"
                @click.native="handlePickerButtonClick"
                size="small"
                variant="tertiary"
                data-ec="manage_google_drive_modal"
                data-et="select_folder_button"
              >
                {{ folderButtonText }}
              </BaseButton>
              </div>
            </div>
          </VGrid>
          <hr>
        </div>
      </VForm>
      <template v-slot:cta>
        <div>
          <BaseButton
            v-if="integration.instance"
            @click.native="confirmDisconnection"
            variant="tertiary"
            size="small"
            data-ec="manage_google_drive_modal"
            data-et="disconnect_button"
          >
            <span>{{ $t('companySettings.integrations.disconnect.disconnect') }}</span>
          </BaseButton>
          <BaseButton
            @click.native="emitClose"
            class="float-right"
            size="small"
            data-ec="manage_google_drive_modal"
            data-et="save_button"
          >
            <span>{{ $t('functional.save') }}</span>
          </BaseButton>
          <BaseButton
            @click.native="emitClose"
            variant="invisible"
            class="float-right"
            size="small"
            subtype="submit"
            data-ec="manage_google_drive_modal"
            data-et="cancel_button"
          >
            <span>{{ $t('functional.cancel') }}</span>
          </BaseButton>
        </div>
      </template>
    </ManageIntegrationBase>
</template>

<script>
import Moment from 'moment';
import { Loading } from 'element-ui';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import VForm from '@/components/deprecated/organisms/Form.vue';
import VGrid from '@/components/deprecated/organisms/Grid.vue';
import ManageIntegrationBase from '@/views/modals/integrations/ManageIntegrationBase.vue';
import integrationsManagementMixin from '@/mixins/integrationsManagementMixin';

export default {
  name: 'ManageGoogleDrive',
  components: {
    ManageIntegrationBase,
    VForm,
    VGrid,
    BaseButton,
  },
  props: {
    show: {
      type: [Boolean],
      required: true,
    },
    showAuthSuccess: {
      type: [Boolean],
      required: true,
    },
    integration: {
      type: [Object],
      required: true,
    },
  },
  mixins: [
    integrationsManagementMixin,
  ],
  data() {
    return {
      settings: {
        folder_id: '',
        folder_name: '',
      },
      oauthToken: '',
      tokenExpiresAt: null,
      pickerApiLoaded: false,
      scope: 'https://www.googleapis.com/auth/drive.file',
      gisClient: null,
    };
  },
  mounted() {
    if (this.hasInstance && this.hasSettings) {
      this.settings = this.integration.instance.settings;
    }

    this.setupGis();
    this.setupPicker();
  },
  watch: {
    hasSettings() {
      if (this.hasInstance) {
        this.copyIntegrationSettings();
      }
    },
  },
  computed: {
    googleDriveFolder() {
      return this.settings.folder_name || this.$t('companySettings.integrations.googledrive.noFolderMessage');
    },
    folderButtonText() {
      if (this.settings.folder_id) {
        return this.$t('companySettings.integrations.googledrive.changeFolder');
      }
      return this.$t('companySettings.integrations.googledrive.chooseFolder');
    },
  },
  methods: {
    confirmDisconnection() {
      this.$emit('disconnect', this.$t('companySettings.integrations.disconnect.warning', { name: this.integration.title }), this.integration.slug);
      this.$emit('close');
    },
    setupGis() {
      const clientScript = document.createElement('script');
      clientScript.setAttribute('src', 'https://accounts.google.com/gsi/client');
      document.head.appendChild(clientScript);

      clientScript.onload = () => {
        /* eslint-disable no-undef */
        this.gisClient = google.accounts.oauth2.initTokenClient({
          client_id: this.integration.client_id,
          scope: this.scope,
          callback: this.handleAuthResult,
          ux_mode: 'popup',
        });
      };
    },
    /**
     * Append GoogelApis script and load picker
     */
    setupPicker() {
      const googlePickerScript = document.createElement('script');
      googlePickerScript.setAttribute('src', 'https://apis.google.com/js/api.js');
      document.head.appendChild(googlePickerScript);

      googlePickerScript.onload = () => {
        if (!window.gapi) {
          // eslint-disable-next-line
          return console.warn('Google API not loaded');
        }

        window.gapi.load('picker', () => {
          this.pickerApiLoaded = true;
        });

        return true;
      };
    },
    /**
     * Authorise GoogleApis when picker button clicked
     */
    handlePickerButtonClick() {
      // if we already have a token and that token has not expired, create picker, else request for new token
      if (this.oauthToken && this.tokenExpiresAt && Moment().isBefore(this.tokenExpiresAt)) {
        this.createPicker();
      } else {
        this.gisClient.requestAccessToken();
      }
    },
    /**
     * Callback from Google Auth2 authorisation
     * Create picker if authorised
     */
    handleAuthResult(authResult) {
      if (authResult && !authResult.error) {
        this.oauthToken = authResult.access_token;
        this.tokenExpiresAt = Moment().add(authResult.expires_in, 'seconds');
        this.createPicker();
      } else {
        // eslint-disable-next-line
        console.warn(authResult.details);
      }
    },
    /**
     * Create a new Google Picker allowing only the selection of folders
     */
    createPicker() {
      /* eslint-disable no-undef */
      const foldersView = new google.picker.DocsView()
        .setIncludeFolders(true)
        .setMimeTypes('application/vnd.google-apps.folder')
        .setSelectFolderEnabled(true);

      if (this.pickerApiLoaded && this.oauthToken) {
        const picker = new google.picker.PickerBuilder()
          .addView(foldersView)
          .setMaxItems(1)
          .setOAuthToken(this.oauthToken)
          .setCallback(this.pickerCallback)
          .build();

        picker.setVisible(true);
      }
    },
    /**
     * Update folder settings are user selects folder in Google Picker
     */
    pickerCallback(callbackData) {
      if (callbackData[google.picker.Response.ACTION] === google.picker.Action.PICKED) {
        // User has successfully selected a folder
        const doc = callbackData[google.picker.Response.DOCUMENTS][0];
        this.settings.folder_id = doc[google.picker.Document.ID];
        this.settings.folder_name = doc[google.picker.Document.NAME];
        this.updateSettings();
      }
    },
    /**
     * Persist new folder settings
     */
    updateSettings() {
      const loadingInstance = Loading.service();

      const { settings } = this;

      this.$store.dispatch('integrations/UPDATE_INSTANCE_SETTINGS', {
        slug: 'googledrive',
        settings,
      }).finally(() => {
        loadingInstance.close();
      });
    },
    emitClose() {
      this.$emit('close');
    },
    copyIntegrationSettings() {
      if (this.integration.instance.settings) {
        this.settings.folder_id = this.integration.instance.settings.folder_id;
        this.settings.folder_name = this.integration.instance.settings.folder_name;

        return;
      }

      this.settings.folder_id = null;
      this.settings.folder_name = null;
    },
  },
};
</script>
